import { useEffect, useState } from "react";

import {
  Button,
  Callout,
  Classes,
  Colors,
  Dialog,
  Intent,
  Spinner,
} from "@blueprintjs/core";
import styled from "@emotion/styled";
import findIndex from "lodash/findIndex";

import { Box, Flex } from "@/components/layout/flexbox";
import { StandaloneCheckbox } from "@/components/pieces/form/StandaloneCheckbox";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  CalendarAccountsDocument,
  CalendarsQuery,
  useCalendarsLazyQuery,
  useSetExternalCalendarMutation,
} from "@/graphql";
import { CalendarAccount } from "@/types/calendars";
import { NonNullableArrayItem } from "@/types/utils";
import { ensureNonNullArray } from "@/utils/array";

type Calendar = NonNullableArrayItem<CalendarAccount["calendars"]>;

type CalendarQuery = NonNullableArrayItem<
  NonNullable<CalendarsQuery["calendars"]>
>;

function mapCalendarRenderListFromCalendarList(
  calendarList: CalendarQuery[],
  selectedCalendar: Calendar[] = [],
) {
  return calendarList
    .map((calendar) => {
      return {
        ...calendar,
        isSelected:
          findIndex(
            selectedCalendar,
            (item) => item.calendarId === calendar.calendarId,
          ) !== -1,
      };
    })
    .sort((calendar) => (calendar.isPrimary ? -1 : 1));
}

type EnhancedCalendarQuery = CalendarQuery & {
  isSelected: boolean;
};

interface CalendarSettingDialogProps {
  visible: boolean;
  onClose: () => void;
  setErrorMessage: (message: string) => void;
  selectedCalendarAccount: CalendarAccount;
  setSaved: () => void;
}

export function CalendarSettingDialog({
  visible,
  onClose,
  setErrorMessage,
  selectedCalendarAccount,
  setSaved,
}: CalendarSettingDialogProps) {
  const [calendarList, setCalendarList] = useState<EnhancedCalendarQuery[]>([]);

  const [fetch, { loading }] = useCalendarsLazyQuery({
    onCompleted: (response) => {
      const calendars = ensureNonNullArray(response.calendars);
      if (calendars) {
        setCalendarList(
          mapCalendarRenderListFromCalendarList(
            calendars,
            ensureNonNullArray(selectedCalendarAccount.calendars),
          ),
        );
      }
    },
    onError: () => {
      onClose();
      setErrorMessage("Something went wrong");
    },
  });

  const [setExternalCalendar, { loading: saving }] =
    useSetExternalCalendarMutation({
      onError: () => {
        onClose();
        setErrorMessage("Something went wrong");
      },
      onCompleted: (response) => {
        if (!response || !response.setExternalCalendar) {
          return;
        }
        const {
          setExternalCalendar: { error },
        } = response;
        if (error && error.message) {
          setErrorMessage(error.message);
        } else {
          setSaved();
        }
        onClose();
      },
      refetchQueries: [
        {
          query: CalendarAccountsDocument,
        },
      ],
    });

  useEffect(() => {
    if (visible && selectedCalendarAccount && selectedCalendarAccount.id) {
      fetch({
        variables: {
          calendarAccountId: selectedCalendarAccount.id,
        },
      });
    }
  }, [visible, selectedCalendarAccount]);

  const onPressCalendar = (calendarIndex: number, e: React.MouseEvent) => {
    e.preventDefault();
    setCalendarList(
      calendarList.map((calendar, index) => {
        return {
          ...calendar,
          isSelected:
            calendarIndex === index
              ? !calendar.isSelected
              : calendar.isSelected,
        };
      }),
    );
  };

  const onConfirm = () => {
    const calendars = calendarList
      .filter((calendar) => calendar.isSelected)
      .map((calendar) => {
        return {
          name: calendar.name,
          id: calendar.calendarId,
        };
      });
    setExternalCalendar({
      variables: {
        calendarAccountId: selectedCalendarAccount.id,
        calendars: calendars,
      },
    });
  };

  return (
    <StyledDialog
      isOpen={visible}
      onClose={() => onClose()}
      lazy={true}
      title="Connection Settings"
      width="480px"
    >
      <StyledContainerList
        pb={16}
        px={20}
        fontSize="16"
        backgroundColor={Colors.WHITE}
        className="bas"
      >
        <Box mt={3} fontWeight={500}>
          Check for Schedule Conflicts
        </Box>
        <Box mt={2} color={SUBTEXT_COLOR}>
          Your connected account has multiple calendars. Please select all
          calendars that you want us to check for scheduling conflicts.
        </Box>
        <Box mt={2} color={SUBTEXT_COLOR}>
          If no calendars were selected, then your default calendar will be
          used.
        </Box>

        {loading || saving ? (
          <Box my={3}>
            <Spinner intent={Intent.PRIMARY} />
          </Box>
        ) : (
          calendarList.map((calendar, index) => (
            <Flex
              key={calendar.id}
              my={3}
              onClick={(e) => onPressCalendar(index, e)}
              css={{ cursor: "pointer" }}
            >
              <StandaloneCheckbox checked={calendar.isSelected} />
              <Flex ml={3} flexWrap="wrap">
                <Box mr={2}>{calendar.name}</Box>
                {calendar.isPrimary && (
                  <Box color={SUBTEXT_COLOR}>{"(Default)"}</Box>
                )}
              </Flex>
            </Flex>
          ))
        )}

        <Callout style={{ backgroundColor: "#E9F1FB" }} icon={null}>
          💡Tip: Events on your calendar must be set to “Busy” in order to block
          off time.
        </Callout>
      </StyledContainerList>
      <div className={Classes.DIALOG_FOOTER}>
        <Button intent="primary" disabled={false} onClick={onConfirm}>
          {"Confirm"}
        </Button>
      </div>
    </StyledDialog>
  );
}

const StyledDialog = styled(Dialog)<{ width: string }>`
  &.${Classes.DIALOG} {
    max-width: 90%;
    padding-bottom: 0;
    border-radius: 12px;
    .${Classes.DIALOG_HEADER} {
      box-shadow: none;
      padding: 0px 16px;
      background-color: ${Colors.WHITE};
    }

    .${Classes.DIALOG_FOOTER} {
      box-shadow: none;
      margin: 0;
      padding: 10px 16px;
      border-radius: 12px;
      background-color: ${Colors.WHITE};
    }
  }
`;

const StyledContainerList = styled(Box)`
  max-height: 70vh;
  overflow-y: auto;

  .${Classes.BUTTON} {
    padding: 12px 19px;
    margin-right: 10px;
  }
  .${Classes.BUTTON_TEXT} {
    font-size: 16px;
  }

  .${Classes.FORM_GROUP} {
    margin: 0;
  }

  label.${Classes.CONTROL}.${Classes.CHECKBOX} {
    .${Classes.CONTROL_INDICATOR} {
      font-size: 24px !important;
    }
  }
`;
